import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { Popover } from '@bingads-webui-react/popover';
import { FluctuationService } from '@bingads-webui-campaign/fluctuation-service';
import { ExpLoggerFactory } from '@bingads-webui/experiment-logger';
import { Loading } from '@bingads-webui-react/loading';
import { MetricCounter, DescriptionContainer, CalloutWithAutoPlacement, InsightAction, ActionSubPanel } from '@bingads-webui-campaign/fluctuation-common';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { instrumentationSchema } from './fluctuation-instr-schema';
import { Pagination } from './common/pagination';
import { ActionContainer } from './fluctuation-action/ActionContainer';
import { FluctuationLogService } from './fluctuation-log-service';
import { MinFluctuationsCountToShowAllInsightsButton, FluctuationGranularity, FluctuationType } from './common/constants';
import { ViewAllContainer } from './fluctuation-action/ViewAllContainer';
import { generateDescription, flattenFluctuationsDict, getFluctuationMetricUtil } from './common/utils';

const AB_TEST_NAME = 'AB_FluctuationBulb';

export class FluctuationPopup extends React.PureComponent {
  constructor(props) {
    super(props);
    const {
      deps, page, fluctuation: dateFluxMap, fromScenario, perfMarker,
    } = this.props;

    if (perfMarker) {
      this.perfMarker = perfMarker.createChild('Fluctuation_Popup');
      this.perfMarker.willInit();
    }

    const isDateRange = _.isArray(dateFluxMap);
    const flattenedDateFlux = isDateRange ?
      flattenFluctuationsDict(dateFluxMap) :
      flattenFluctuationsDict([dateFluxMap]);

    this.state = {
      showFluctuation: true,
      page: flattenedDateFlux[page] ? page : 0,
      dateFluctuations: flattenedDateFlux,
      totalPages: flattenedDateFlux.length,
      isLoading: _.isEmpty(flattenedDateFlux),
      currentSubPanel: ActionSubPanel.None,
      expandedEntityName: '',
      entityDetails: [],
      popupOpened: false,
    };
    this.initializeInstrumentation();
    this.logService = new FluctuationLogService(fromScenario);
    this.fromScenario = fromScenario;
    this.expLogger = ExpLoggerFactory.createLogger(AB_TEST_NAME);
    this.bulbColor = 'Yellow';
    this.token = deps.token || deps.context.Token;
  }

  componentDidMount() {
    const { withoutOpporCheck } = this.props;
    const { isLoading } = this.state;

    if (isLoading && !withoutOpporCheck) {
      this.fetchData();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { withoutOpporCheck, fluctuation } = this.props;

    if (withoutOpporCheck && this.state.popupOpened && !prevState.popupOpened) {
      this.fetchData();
    }

    if (_.isEmpty(prevProps.fluctuation) && !_.isEmpty(fluctuation)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        dateFluctuations: flattenFluctuationsDict([fluctuation]),
        isLoading: false,
      });
    }
    if (this.perfMarker) {
      this.perfMarker.done();
    }
  }

  getFluctuation = () => this.state.dateFluctuations[this.state.page].Fluctuation;

  getDate = page => this.state.dateFluctuations[page].Date;

  getFluctuationMetric = (flucType) => {
    const metric = getFluctuationMetricUtil(flucType);
    if (metric === null) {
      this.activities.userActionActivity
        .error(
          `Invalid Fluctuation Type: ${flucType}`,
          'FluctuationError'
        );
    }
    return metric;
  };

  fetchData() {
    if (this.perfMarker) this.perfMarker.willFetchData();
    const {
      deps: { context, appConfig },
      asyncLoadFluctuationConfig: { dateRange, fluctuationTypeIds },
      updateFlucData,
      updateLoadingStatus,
    } = this.props;
    if (updateLoadingStatus) {
      updateLoadingStatus(true);
    }
    const flucServiceIns = new FluctuationService({
      token: this.token,
      options: {
        userId: context.CurrentUser.Id,
        customerId: context.CurrentCustomer.Id,
        campaignId: context.CurrentCampaign &&
          context.CurrentCampaign.Id,
        accountId: context.CurrentAccount.Id,
        granularity: FluctuationGranularity.Day,
        startDate: this.formatDateString(dateRange.startDate),
        endDate: this.formatDateString(dateRange.endDate),
        surfacingChannelId: context.CurrentTabId === 'Campaigns' ? 1 : '',
        // TODO: confirm whether isFluctuationsExperiementEnabled is GAed
        isFluctuationsExperiementEnabled: context.isFluctuationsExperiementEnabled,
        host: appConfig && appConfig.get ? appConfig.get('BingAdsAdInsightWebApiUri') : undefined,
      },
    });
    flucServiceIns.findAll()
      .then((successResponse) => {
        if (updateFlucData) {
          updateFlucData(successResponse);

          if (updateLoadingStatus) {
            updateLoadingStatus(false);
          }
        } else {
          const unfilteredDateFlux = flattenFluctuationsDict(successResponse);
          const filteredDateFlux = _.filter(
            unfilteredDateFlux,
            dateFlux => _.contains(
              fluctuationTypeIds,
              FluctuationType[dateFlux.Fluctuation.FluctuationType]
            )
          );

          /* istanbul ignore else */
          if (_.isEmpty(filteredDateFlux)) {
            this.activities.userActionActivity
              .error(
                `Load empty fluctuaion: cid: ${context.CurrentCustomer.Id}, aid: ${context.CurrentAccount.Id}, campaignId: ${context.CurrentCampaign &&
                context.CurrentCampaign.Id}, fluc type: ${fluctuationTypeIds.toString()}`,
                'LoadFluctuationError'
              );
          }

          this.setState({
            dateFluctuations: filteredDateFlux,
            totalPages: filteredDateFlux.length,
            isLoading: false,
          });
        }
      }, (errorResponse) => {
        this.activities.userActionActivity
          .error(
            `Fail to load Fluctuations: ${JSON.stringify(errorResponse)}`,
            'LoadFluctuationError'
          );
        this.setState({ isLoading: false });
      });
  }

  triggerOnOpen = (page) => {
    if (!_.isEmpty(this.state.dateFluctuations)) {
      const action = `[date] ${this.getDate(page)} [action] Open ${this.bulbColor}`;
      this.expLogger.log(action, this.flight);

      if (this.props.onFlucOpen) {
        const currentFluctuationType = this.getFluctuation().FluctuationType;
        this.props.onFlucOpen(
          this.getFluctuationMetric(currentFluctuationType),
          this.getFluctuation().UniqueId
        );
      }
    }
  };

  resetState = () => {
    this.setState({
      page: 0,
      showFluctuation: true,
      popupOpened: true,
    });
    this.triggerOnOpen(0);
    if (!_.isEmpty(this.state.dateFluctuations)) {
      this.logService.logContext(
        this.getFluctuation(),
        this.getDate(this.state.page),
        'FluxClick',
        this.props.deps.context.CurrentUser.Id,
        this.props.deps.context.CurrentUser.IsInternal,
        this.activities.userActionActivity,
        'FluctuationAction'
      );
      this.activities.userActionActivity
        .trace(
          `Fluctuation popup opened: Date: ${this.getDate(this.state.page)},
          FlucId: ${this.getFluctuation().UniqueId}`,
          'FluctuationAction'
        );
    }
  };

  closeFluctuation = () => {
    this.setState({
      showFluctuation: false,
    });
    this.activities.userActionActivity
      .trace(
        `Fluctuation popup closed: Date: ${this.getDate(this.state.page)}:
          Fluctuation Unique ID: ${this.getFluctuation().UniqueId}`,
        'FluctuationAction'
      );
    this.props.onClose();
  };

  formatDateString(dateStr) {
    const leftPadding = s => ((s.length === 1) ? `0${s}` : s);
    const dt = new Date(Date.parse(dateStr));
    const year = `${dt.getFullYear()}`;
    const month = `${dt.getMonth() + 1}`;
    const day = `${dt.getDate()}`;

    return `${year}-${leftPadding(month)}-${leftPadding(day)}`;
  }

  selectPage = (page) => {
    // should reset currentSubPanel and currentEntityDetails to initialize new page
    this.setState({
      page,
      currentSubPanel: ActionSubPanel.None,
      expandedEntityName: '',
      entityDetails: [],
    });
    this.triggerOnOpen(page);
  };

  initializeInstrumentation = () => {
    this.props.deps.instrumentation.addScenario(instrumentationSchema);

    const fluctuationScenario = this.props.deps.instrumentation.fluctuation.create();
    this.activities = {
      feedbackActivity: fluctuationScenario.fluctuationFeedback.create(),
      userActionActivity: fluctuationScenario.userAction.create(),
      dismissActivity: fluctuationScenario.fluctuationDismiss.create(),
    };
  };

  submitLog = (log) => {
    const { appConfig, context } = this.props.deps;
    const currentFluctuation = this.getFluctuation();
    const currentDate = this.getDate(this.state.page);
    const fluctuationData = {
      Comment: log.comment,
      Feedbacks: log.type === ActionSubPanel.FeedbackPanel && log.feedbacks ?
        log.feedbacks.map((item) => {
          if (item === '0') {
            return 'FluctuationIsGood';
          } else if (item === '1') {
            return 'ReasonIsIncorrect';
          }
          return 'ActionIsNotHelpful';
        }) : [],
      DismissFeedbacks: log.type === ActionSubPanel.DismissPanel && log.feedbacks ?
        log.feedbacks.map((item) => {
          if (item === '0') {
            return 'KnowTheCause';
          } else if (item === '1') {
            return 'FollowedUpAlready';
          }
          return 'JustWantToRemove';
        }) : [],
    };

    if (log.type === ActionSubPanel.DismissPanel) {
      this.logService.logContext(
        this.getFluctuation(),
        currentDate,
        'FluxDismiss',
        context.CurrentUser.Id,
        context.CurrentUser.IsInternal,
        this.activities.userActionActivity,
        'FluctuationDismiss',
        fluctuationData
      );

      // post dismiss request to MT
      const flucServiceIns = new FluctuationService({
        token: this.token,
        options: {
          userId: context.CurrentUser.Id,
          customerId: context.CurrentCustomer.Id,
          campaignId: context.CurrentCampaign &&
            context.CurrentCampaign.Id,
          accountId: context.CurrentAccount.Id,
          uniqueId: currentFluctuation.UniqueId,
          datetime: currentDate,
          host: appConfig && appConfig.get ? appConfig.get('BingAdsAdInsightWebApiUri') : undefined,
        },
      });

      const { dateFluctuations } = this.state;
      const actingFluctuation = dateFluctuations.slice(); // protective clone
      actingFluctuation.splice(this.state.page, 1);

      flucServiceIns.postFeedback().then(() => {
        this.props.onFlucDismiss({
          date: currentDate,
          fluctuation: currentFluctuation,
          isLastFluc: this.state.totalPages === 1,
        });
        this.setState({
          totalPages: this.state.totalPages - 1,
          dateFluctuations: actingFluctuation,
          page: this.state.page < actingFluctuation.length ?
            this.state.page :
            this.state.page - 1,
        });
      }, (errorResponse) => {
        this.activities.userActionActivity
          .error(
            `Fail to dismiss Fluctuations: ${JSON.stringify(errorResponse)}`,
            'DismissFluctuationError'
          );
      });
    } else {
      this.logService.logContext(
        currentFluctuation,
        currentDate,
        'FluxFeedback',
        context.CurrentUser.Id,
        context.CurrentUser.IsInternal,
        this.activities.userActionActivity,
        'FluctuationFeedback',
        fluctuationData
      );
    }
  };

  render() {
    const {
      canViewAllInsights, deps, metrics,
      triggerButton, isOpen, onDismiss, isReactNodeTriggerButton, withoutOpporCheck, withPopoverAdapter,
    } = this.props;
    const fluxLength = this.state.dateFluctuations.length;

    if (!this.state.isLoading && fluxLength === 0) {
      return null;
    }
    const currentFluctuation = !this.state.isLoading && this.getFluctuation();
    const shouldShowViewAllButton = canViewAllInsights
      && fluxLength >= MinFluctuationsCountToShowAllInsightsButton;
    const closeButtonClass = 'iconba iconba-Cancel';
    const closeButton = isReactNodeTriggerButton && this.state.showFluctuation ? (
      /* eslint-disable jsx-a11y/click-events-have-key-events,
        jsx-a11y/no-static-element-interactions */
      <div
        tabIndex="0"
        role="button"
        aria-label="Close"
        onClick={this.closeFluctuation}
        className={`${closeButtonClass} fluctuation-close`}
      />
    ) :
      null;
    const viewAllContainer = shouldShowViewAllButton ? (<ViewAllContainer
      deps={deps}
    />) : null;
    const pagination = this.state.totalPages > 1 ? (<Pagination
      onClickHandler={this.selectPage}
      totalPages={this.state.totalPages}
      currentPage={this.state.page}
    />) : null;
    const PopoverAdapter = isReactNodeTriggerButton ? Popover : CalloutWithAutoPlacement;
    const Wrapper = withPopoverAdapter ? PopoverAdapter : React.Fragment;

    if (!this.state.isLoading && this.state.showFluctuation) {
      const action = `[date] ${this.getDate(this.state.page)} [action] Show ${this.bulbColor}`;
      this.expLogger.log(action, this.flight);
    }

    const metricWithInsightAsDefault = _.map(metrics, metric => ({ line: 'iconba iconba-Insights', ...metric }));
    const detailEntityUniqueId = `${this.state.expandedEntityName}-${(this.state.entityDetails || []).length}`;
    const calloutKey = `${currentFluctuation && (currentFluctuation || {}).UniqueId}-${this.state.currentSubPanel}-${detailEntityUniqueId}`;

    return (
      <Wrapper
        // calloutKey only impact <CalloutWithAutoPlacement/> to solve arrow position issue
        calloutKey={calloutKey}
        trigger="click"
        target={triggerButton}
        placement="auto"
        isOpen={isOpen}
        onOpen={this.resetState}
        onDismiss={onDismiss}
        customKeepOpen={({ target }) => target.className === 'link-text'}
        keepTargetPropagation
      >
        {
          (this.state.isLoading && !withoutOpporCheck) ?
            <div className="fluctuation-loading"><Loading text={deps.i18n.getString('Loading')} /></div> :
            <div className="bingads-fluctuation-wrapper">{this.state.isLoading ? null : (
              <React.Fragment>
                <div className="fluctuation-upper-content">
                  <div className="bold">
                    {deps.i18n.formatDate(new Date(this.getDate(this.state.page)), { skeleton: 'yMEd' })}
                  </div>
                  <MetricCounter
                    metrics={metricWithInsightAsDefault}
                  />
                  {closeButton}
                </div>
                <DescriptionContainer
                  description={generateDescription({
                    description: currentFluctuation.Description,
                    deps,
                  })}
                />
                <ActionContainer
                  deps={_.extend(deps, this.activities)}
                  page={this.state.page}
                  fluctuation={currentFluctuation}
                  date={this.getDate(this.state.page) || {}}
                  context={{
                    CustomerId: currentFluctuation.CustomerId,
                    AccountId: currentFluctuation.AccountId,
                    CampaignId: currentFluctuation.CampaignId,
                    AdGroupId: currentFluctuation.AdGroupId,
                  }}
                  defaultEntityDetails={{
                    expandedEntityName: this.state.expandedEntityName,
                    entityDetails: this.state.entityDetails,
                  }}
                  onDetailContainerShow={
                    ({ expandedEntityName, entityDetails }) =>
                      this.setState({ expandedEntityName, entityDetails })
                  }
                  fromScenario={this.fromScenario}
                />
                <div className="fluctuation-lower-content">
                  <InsightAction
                    deps={deps}
                    show={this.state.showAction}
                    onSubmit={this.submitLog}
                    activeSubPanel={this.state.currentSubPanel}
                    onCurrentSubPanelChange={currentSubPanel => this.setState({ currentSubPanel })}
                  />
                </div>
                {viewAllContainer}
                {pagination}
              </React.Fragment>)}
            </div>
        }
      </Wrapper>
    );
  }
}

const Fluctuation = PropTypes.shape({
  Date: PropTypes.string,
  FluctuationsPerDate: PropTypes.arrayOf(PropTypes.shape({
    Description: PropTypes.shape({
      MessageId: PropTypes.string,
      Parameters: PropTypes.arrayOf(PropTypes.shape({
        Values: PropTypes.arrayOf(PropTypes.string),
      })),
    }),
    Actions: PropTypes.arrayOf(PropTypes.shape({
      ActionId: PropTypes.number,
      Message: PropTypes.shape({
        MessageId: PropTypes.string,
        Parameters: PropTypes.arrayOf(PropTypes.shape({
          Type: PropTypes.string,
          UrlId: PropTypes.string,
          DisplayName: PropTypes.string,
          Values: PropTypes.arrayOf(PropTypes.string),
        })),
      }),
    })),
    RootCauses: PropTypes.arrayOf(PropTypes.shape({
      RootCauseId: PropTypes.number,
      Message: PropTypes.shape({
        MessageId: PropTypes.string,
        Parameters: PropTypes.arrayOf(PropTypes.shape({
          DisplayName: PropTypes.string,
          UrlId: PropTypes.string,
          Type: PropTypes.string,
          Values: PropTypes.arrayOf(PropTypes.string),
          SubParameters: PropTypes.arrayOf(PropTypes.shape({
            DisplayName: PropTypes.string,
            UrlId: PropTypes.string,
            Type: PropTypes.string,
            Values: PropTypes.arrayOf(PropTypes.string),
          })),
        })),
      }),
    })),
    length: PropTypes.number,
  })),
});

FluctuationPopup.propTypes = {
  deps: PropTypes.shape({
    token: PropTypes.string,
    imagePath: PropTypes.string,
    /* To get correct CI environment host name and port for FluctuationService */
    appConfig: PropTypes.shape({
      get: PropTypes.func,
    }),
    instrumentation: PropTypes.shape({
      addScenario: PropTypes.func,
      fluctuation: PropTypes.shape({
        create: PropTypes.func,
      }),
    }),
    i18n: PropTypes.shape({
      getString: PropTypes.func,
      formatDate: PropTypes.func,
    }).isRequired,
    context: PropTypes.shape({
      CurrentCustomer: PropTypes.shape({
        Id: PropTypes.number,
      }),
      CurrentAccount: PropTypes.shape({
        Id: PropTypes.number,
      }),
      CurrentCampaign: PropTypes.shape({
        Id: PropTypes.number,
      }),
      CurrentUser: PropTypes.shape({
        Id: PropTypes.number,
        IsInternal: PropTypes.bool,
      }),
      scope: PropTypes.shape({
        save: PropTypes.func,
      }),
      Token: PropTypes.string,
      CurrentTabId: PropTypes.string,
      isFluctuationsExperiementEnabled: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  metrics: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string,
    line: PropTypes.string,
    color: PropTypes.string,
  })).isRequired,
  page: PropTypes.number,
  isOpen: PropTypes.bool,
  onFlucOpen: PropTypes.func,
  onDismiss: PropTypes.func,
  onFlucDismiss: PropTypes.func,
  onClose: PropTypes.func,
  fluctuation: PropTypes.oneOfType([
    Fluctuation,
    PropTypes.arrayOf(Fluctuation),
  ]),
  isReactNodeTriggerButton: PropTypes.bool,
  triggerButton: PropTypes.oneOfType([
    PropTypes.instanceOf(Element),
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  canViewAllInsights: PropTypes.bool,
  asyncLoadFluctuationConfig: PropTypes.shape({
    dateRange: PropTypes.shape({
      startDate: PropTypes.instanceOf(Date).isRequired,
      endDate: PropTypes.instanceOf(Date).isRequired,
    }),
    fluctuationTypeIds: PropTypes.arrayOf(PropTypes.string),
  }),
  fromScenario: PropTypes.string,
  withoutOpporCheck: PropTypes.bool,
  updateFlucData: PropTypes.func,
  updateLoadingStatus: PropTypes.func,
  perfMarker: perfMarkerPropTypes.isRequired,
  withPopoverAdapter: PropTypes.bool,
};

FluctuationPopup.defaultProps = {
  onFlucOpen: () => { },
  onDismiss: () => { },
  onFlucDismiss: () => { },
  onClose: () => { },
  page: 0,
  isOpen: false,
  canViewAllInsights: false,
  isReactNodeTriggerButton: true,
  asyncLoadFluctuationConfig: {},
  fluctuation: {},
  fromScenario: '',
  withoutOpporCheck: false,
  updateFlucData: undefined,
  updateLoadingStatus: undefined,
  withPopoverAdapter: true,
};

